import { orderPeople, searchPeople } from '@echo/core';
import { useGroupInvites, useGroupPeople, useMe, useMyGroup } from '@echo/core/react';
import { SearchField } from '@echo/ui-web';
import { plural } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Search } from 'lucide-react';
import { useState } from 'react';
import { ToolbarBack } from '@/components/empty-state-frame';
import { GroupLoadFailed, GroupLoading } from '@/components/group-load-state';
import { GroupPersonRow } from '@/components/group-person-row';
import { PendingInvites } from '@/components/pending-invites';
import { useGoBack } from '@/lib/use-go-back';

export const Route = createFileRoute('/_app/account_/groups_/$id_/people')({
  component: PeopleAndAccessPage,
});

/**
 * Manage group › People and access (Figma account/groups): everyone in the group with their
 * role badge, you first, searchable by name; a person opens their member profile. Its members
 * see it too, as Members (core#748 shows them each other's names): the people only, without
 * who has been invited — below them for a leader (Pending invites), matched by address.
 */
function PeopleAndAccessPage() {
  const { t } = useLingui();
  const navigate = useNavigate();
  const goBack = useGoBack(
    () => void navigate({ to: '/account/groups/$id', params: { id }, replace: true }),
  );
  const me = useMe();
  const { id } = Route.useParams();
  const group = useGroupPeople(id);
  const manages = useMyGroup(id).group?.manages ?? false;
  const invites = useGroupInvites(id, manages).data ?? [];
  const [query, setQuery] = useState('');
  const toolbar = (
    <ToolbarBack
      render={({ className, children }) => (
        <button type="button" className={className} onClick={goBack}>
          {children}
        </button>
      )}
    />
  );
  const heading = (
    <h1 className="text-display-sm font-bold">
      {manages ? <Trans>Members and Access</Trans> : <Trans>Members</Trans>}
    </h1>
  );
  if (group.isPending) {
    return (
      <>
        {toolbar}
        {heading}
        <GroupLoading />
      </>
    );
  }
  if (group.isError) {
    return (
      <>
        {toolbar}
        {heading}
        <div className="pt-content-md">
          <GroupLoadFailed onRetry={() => void group.refetch()} />
        </div>
      </>
    );
  }

  const fullName = me.data?.name ?? me.data?.firstName ?? '';
  const people = searchPeople(orderPeople(group.data), query, fullName);
  const results = people.length;
  const search = query.trim().toLowerCase();
  const invited = invites.filter((invite) => invite.address.toLowerCase().includes(search));
  return (
    <>
      {toolbar}
      {heading}
      <div className="flex flex-col gap-section-sm pt-content-md">
        <SearchField
          label={t`Search by name or email`}
          icon={<Search size={24} className="shrink-0 text-icon-medium" aria-hidden />}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
        <section className="flex flex-col gap-content-sm rounded-xl border border-border-weak bg-surface px-content-sm py-content-md">
          {/* Read out as the search narrows the list. */}
          <p aria-live="polite" className="text-body-sm text-on-surface-medium">
            {plural(results, { one: '# result', other: '# results' })}
          </p>
          <ul className="flex flex-col">
            {people.map((person, index) => (
              <li key={person.id}>
                <GroupPersonRow
                  person={person}
                  name={person.you ? t`${fullName} (You)` : person.name}
                  last={index === people.length - 1}
                  onPress={() =>
                    void navigate({
                      to: '/account/groups/$id/member/$personId',
                      params: { id, personId: person.id },
                    })
                  }
                />
              </li>
            ))}
          </ul>
        </section>
        <PendingInvites invites={invited} groupId={id} />
      </div>
    </>
  );
}
